import { useEffect, useState } from "react";

import { Button, FormGroup, Icon, InputGroup } from "@blueprintjs/core";
import { addYears } from "date-fns";

import { Box, Flex } from "@/components/layout/flexbox";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { SUBTEXT_COLOR } from "@/css/constants";
import { useCloneProgramMutation, useCreateProgramMutation } from "@/graphql";
import { MainProgram, MiniProgram, NewProgram } from "@/types/programs";
import { areDifferentTimes, getBrowserTimezone } from "@/utils/datetime";

interface ValidateProgramInputProps {
  name?: string;
  launchDate?: string | null;
  currentLaunchDate?: string | null;
}

export const validateProgramInput = ({
  name,
  launchDate,
  currentLaunchDate,
}: ValidateProgramInputProps) => {
  if (!name) return { valid: false };
  if (!launchDate) return { valid: false };

  const targetDate = new Date(launchDate);
  const currentDate = currentLaunchDate ? new Date(currentLaunchDate) : null;

  if (
    !(currentDate && !areDifferentTimes(currentDate, targetDate)) &&
    targetDate < new Date()
  ) {
    return {
      valid: false,
      errorType: "launchDate",
      message: "The target launch date must be in the future.",
    };
  }

  return { valid: true };
};

interface AddProgramFormProps {
  onComplete: (program: NewProgram) => void;
  onError: (error: Error) => void;
  mode: "create" | "clone";
  selectedProgram?: MainProgram | MiniProgram;
}

export function AddProgramForm({
  onComplete,
  onError,
  mode,
  selectedProgram,
}: AddProgramFormProps) {
  const [name, setName] = useState("");
  const [launchDate, setLaunchDate] = useState<string | null>(null);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const maxDate = addYears(new Date(), 1);
  const minDate = new Date();

  const [createProgram, { loading: createLoading }] = useCreateProgramMutation({
    onCompleted: (data) => {
      const { ok, program } = data?.createProgram || {};
      if (ok && program) {
        onComplete(program);
      } else {
        onError(Error("Something went wrong. Please try again."));
      }
    },
    onError,
  });

  const [cloneProgram, { loading: cloneLoading }] = useCloneProgramMutation({
    onCompleted: (data) => {
      const { ok, error, program } = data?.cloneProgram || {};
      if (ok && program) {
        onComplete(program);
      } else {
        onError(Error(error || "Something went wrong. Please try again."));
      }
    },
    onError,
  });

  useEffect(() => {
    if (selectedProgram) {
      setName(`[Copy] ${selectedProgram.name}`);
    }
  }, [selectedProgram]);

  const validation = validateProgramInput({ name, launchDate });

  const onCreateProgram = () => {
    if (!validation.valid) {
      if (validation.message) {
        onError({ message: validation.message, name: "ValidationError" });
      }
    } else {
      if (mode === "create" && launchDate) {
        createProgram({ variables: { name, targetLaunchDate: launchDate } });
      } else {
        cloneProgram({
          variables: {
            programId: selectedProgram?.id || "",
            name,
            targetLaunchDate: launchDate!,
          },
        });
      }
    }
  };

  return (
    <Box width={1}>
      <Box fontWeight={600} fontSize={16}>
        Program Name
      </Box>
      <Box fontSize={14} color={SUBTEXT_COLOR} mb={2}>
        Eventually, this will be visible to participants
      </Box>
      <InputGroup
        onChange={(e) => {
          setName(e.target.value);
        }}
        value={name}
        large={true}
        fill={true}
        placeholder="Program Name"
        autoFocus={true}
        disabled={createLoading || cloneLoading}
      />
      <Box fontWeight={600} fontSize={16} mt={3}>
        Target Launch Date
      </Box>
      <Box fontSize={14} color={SUBTEXT_COLOR} mb={2}>
        This is the date your participants will be invited to the program.
        <div>You can adjust this later</div>
      </Box>
      <Box css={{ position: "relative" }}>
        <Icon
          icon="calendar"
          style={{
            position: "absolute",
            top: 12,
            left: 10,
            zIndex: 99,
            color: SUBTEXT_COLOR,
          }}
        />
        <DateTimeInputV2
          disabled={createLoading || cloneLoading}
          onChange={setLaunchDate}
          value={launchDate}
          timezone={timezone}
          onChangeTimezone={setTimezone}
          maxDateTime={maxDate}
          minDateTime={minDate}
        />
      </Box>
      {!validation.valid && validation?.errorType === "launchDate" && (
        <Box color={"#c23030"} fontSize={14} mt={10}>
          {validation?.message}
        </Box>
      )}
      <FormGroup style={{ margin: 0 }}>
        <Flex mt={3}>
          <Button
            loading={createLoading || cloneLoading}
            style={{ minWidth: 160 }}
            large={true}
            intent="primary"
            disabled={!validation.valid}
            onClick={onCreateProgram}
          >
            {mode === "create" ? "Create" : "Continue"}
          </Button>
        </Flex>
      </FormGroup>
    </Box>
  );
}
